import { useEffect, useState } from "react";
import { Progress } from "@shared/components/ui/progress";
import { motion } from "framer-motion";
import { Settings } from "lucide-react";

interface ClinicLoadingScreenProps {
  onComplete?: () => void;
}

const ClinicLoadingScreen = ({ onComplete }: ClinicLoadingScreenProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate loading progress
    const intervals = [
      { target: 30, delay: 200 },
      { target: 60, delay: 600 },
      { target: 85, delay: 1000 },
      { target: 100, delay: 1500 },
    ];

    const timers: ReturnType<typeof setTimeout>[] = [];

    intervals.forEach(({ target, delay }) => {
      const timer = setTimeout(() => {
        setProgress(target);
        if (target === 100 && onComplete) {
          setTimeout(onComplete, 300);
        }
      }, delay);
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/30"
      dir="rtl"
    >
      {/* Animated Icon */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="mb-8"
      >
        <div className="relative">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute inset-0 rounded-full bg-primary/20 blur-xl"
            style={{ width: 120, height: 120, margin: -20 }}
          />
          <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Settings className="w-10 h-10 text-primary-foreground" />
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Loading Text */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-center mb-8"
      >
        <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
          جاري بناء العيادة الإلكترونية
        </h2>
        <p className="text-muted-foreground text-sm">
          يرجى الانتظار قليلاً...
        </p>
      </motion.div>

      {/* Progress Section */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="w-72 md:w-80"
      >
        {/* Progress Bar */}
        <Progress value={progress} className="h-3 mb-3" />
        
        {/* Percentage */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">تحميل البيانات</span>
          <motion.span
            key={progress}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            className="text-lg font-bold text-primary"
          >
            {progress}%
          </motion.span>
        </div>
      </motion.div>

      {/* Decorative Elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.5, duration: 1 }}
        className="absolute bottom-10 text-center"
      >
        <p className="text-xs text-muted-foreground/60">
          منصة العيادات الإلكترونية
        </p>
      </motion.div>
    </motion.div>
  );
};

export default ClinicLoadingScreen;
